import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { validateEmail, validatePassword, validateUsername } from '../utils/validators';

function SignupForm({ onSwitchToLogin }) {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const usernameErr = validateUsername(username);
    if (usernameErr) { setError(usernameErr); return; }
    const emailErr = validateEmail(email);
    if (emailErr) { setError(emailErr); return; }
    const passErr = validatePassword(password);
    if (passErr) { setError(passErr); return; }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await signup(username, email, password);
      onSwitchToLogin();
    } catch (err) {
      setError(err.message || 'Error creating account');
    } finally {
      setLoading(false);
    }
  };

  const passwordStrength = () => {
    if (!password) return { width: '0%', color: 'transparent', label: '' };
    if (password.length < 8) return { width: '33%', color: 'var(--color-error)', label: 'Weak' };
    const hasNumber = /\d/.test(password);
    const hasUpper = /[A-Z]/.test(password);
    const hasSpecial = /[^A-Za-z0-9]/.test(password);
    const score = [hasNumber, hasUpper, hasSpecial, password.length >= 12].filter(Boolean).length;
    if (score >= 3) return { width: '100%', color: 'var(--color-success)', label: 'Strong' };
    if (score >= 1) return { width: '66%', color: 'var(--color-accent)', label: 'Fair' };
    return { width: '33%', color: 'var(--color-error)', label: 'Weak' };
  };

  const strength = passwordStrength();

  return (
    <form onSubmit={handleSubmit} className="auth-form">
      <div className="auth-form-group">
        <label htmlFor="signup-username">Username</label>
        <input
          type="text"
          id="signup-username"
          value={username}
          onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/\s+/g, ''))}
          placeholder="e.g. alex_smith"
          required
          autoComplete="username"
          maxLength={20}
        />
        <small className="auth-field-hint">
          Friends use this to find you so they can shop for your size — lowercase letters, numbers and underscores.
        </small>
      </div>
      <div className="auth-form-group">
        <label htmlFor="signup-email">Email</label>
        <input
          type="email"
          id="signup-email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          required
          autoComplete="email"
        />
      </div>
      <div className="auth-form-group">
        <label htmlFor="signup-password">Password</label>
        <input
          type="password"
          id="signup-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="At least 8 characters"
          required
          autoComplete="new-password"
        />
        {password && (
          <div className="password-strength">
            <div className="password-strength-bar">
              <div
                className="password-strength-fill"
                style={{ width: strength.width, backgroundColor: strength.color }}
              />
            </div>
            <span className="password-strength-label" style={{ color: strength.color }}>
              {strength.label}
            </span>
          </div>
        )}
      </div>
      <div className="auth-form-group">
        <label htmlFor="signup-confirm">Confirm Password</label>
        <input
          type="password"
          id="signup-confirm"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="Confirm your password"
          required
          autoComplete="new-password"
        />
      </div>
      {error && <div className="auth-error">{error}</div>}
      <button type="submit" className="auth-submit" disabled={loading}>
        {loading ? 'Creating account...' : 'Sign Up'}
      </button>
      <p className="auth-switch">
        Already have an account?{' '}
        <button type="button" className="auth-switch-btn" onClick={onSwitchToLogin}>
          Log in
        </button>
      </p>
    </form>
  );
}

export default SignupForm;
